import {
  ApiException,
  DatabaseReportAdminViewModel,
  DatabaseReportInputModel,
  DatabaseReportViewModel,
  DocumentReportAdminViewModel,
  DocumentReportInputModel,
  DocumentReportViewModel,
  FileParameter,
  ReportAdminViewModel,
  ReportInputModel,
  ReportsApi,
  ReportViewModel,
  SendReportInputModel,
} from '@ail/reports-api'
import { createApi } from '@reduxjs/toolkit/query/react'
import { USER_MANAGER } from '../../auth/userManager'
import { Constants } from '../../helpers/Constants'
import { getBaseQuery } from './apiQuery'

const client = new ReportsApi({
  baseUrl: Constants.reportsEndpoint,
  getToken: async () => {
    const user = await USER_MANAGER.getUser()
    return user?.access_token ?? ''
  },
})

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery: getBaseQuery<ApiException>(),
  endpoints: (builder) => ({
    getReportsAdmin: builder.query<ReportAdminViewModel[], void>({
      query: () => client.getReportsAdmin(),
      providesTags: ['reportsAdmin'],
    }),
    getReportAdmin: builder.query<ReportAdminViewModel, { reportId: string }>({
      query: ({ reportId }) => client.getReportAdmin(reportId),
      providesTags: ['reportsAdmin'],
    }),
    deleteReportAdmin: builder.mutation<DatabaseReportAdminViewModel, { formId: string }>({
      query: ({ formId }) => client.deleteReportAdmin(formId),
      invalidatesTags: ['reportsAdmin', 'reports'],
    }),
    postDatabaseReportAdmin: builder.mutation<
      DatabaseReportAdminViewModel,
      { dryRun?: boolean; validate?: boolean; model: DatabaseReportInputModel }
    >({
      query: ({ dryRun, validate, model }) => client.postDatabaseReportAdmin(dryRun ?? null, validate ?? null, model),
      invalidatesTags: ['reportsAdmin', 'reports'],
    }),
    putDatabaseReportAdmin: builder.mutation<
      DatabaseReportAdminViewModel,
      { reportId: string; dryRun?: boolean; validate?: boolean; model: DatabaseReportInputModel }
    >({
      query: ({ reportId, dryRun, validate, model }) =>
        client.putDatabaseReportAdmin(reportId, dryRun ?? null, validate ?? null, model),
      invalidatesTags: ['reportsAdmin', 'reports'],
    }),
    postDocumentReportAdmin: builder.mutation<DocumentReportAdminViewModel, { model: DocumentReportInputModel }>({
      query: ({ model }) => client.postDocumentReportAdmin(model),
      invalidatesTags: ['reportsAdmin', 'reports'],
    }),
    putDocumentReportAdmin: builder.mutation<
      DocumentReportAdminViewModel,
      { reportId: string; model: DocumentReportInputModel }
    >({
      query: ({ reportId, model }) => client.putDocumentReportAdmin(reportId, model),
      invalidatesTags: ['reportsAdmin', 'reports'],
    }),
    uploadDocumentReportAdmin: builder.mutation<
      DocumentReportAdminViewModel,
      { reportId: string; file?: FileParameter }
    >({
      query: ({ reportId, file }) => client.uploadDocumentReportAdmin(reportId, file),
      invalidatesTags: ['reportsAdmin', 'reports'],
    }),
    getReports: builder.query<ReportViewModel[], void>({
      query: () => client.getReports(),
      providesTags: ['reports'],
    }),
    getReport: builder.query<ReportViewModel, { reportId: string; force?: boolean }>({
      query: ({ reportId, force }) => client.getReport(reportId, force ?? false),
      providesTags: ['reports'],
    }),
    sendReport: builder.mutation<void, { reportId: string; model: SendReportInputModel }>({
      query: ({ reportId, model }) => client.sendReport(reportId, model),
    }),
  }),
  tagTypes: ['reportsAdmin', 'reports'],
})

export function isDocumentReport(report: ReportViewModel): report is DocumentReportViewModel {
  return report.kind === 'document' || 'fileName' in report || 'mineType' in report
}

export function isDocumentReportAdmin(report: ReportAdminViewModel): report is DocumentReportAdminViewModel {
  return report.kind === 'document' || 'fileName' in report || 'mineType' in report
}

export function isDocumentReportInput(report: ReportInputModel): report is DocumentReportInputModel {
  return report.kind === 'document' || 'fileName' in report || 'mineType' in report
}

export function isDatabaseReport(report: ReportViewModel): report is DatabaseReportViewModel {
  return report.kind === 'database' || 'fields' in report
}

export function isDatabaseReportAdmin(report: ReportAdminViewModel): report is DatabaseReportAdminViewModel {
  return report.kind === 'database' || 'fields' in report
}

export function isDatabaseReportInput(report: ReportInputModel): report is DatabaseReportInputModel {
  return report.kind === 'database' || 'fields' in report
}

export const {
  useGetReportsAdminQuery,
  useGetReportAdminQuery,
  useDeleteReportAdminMutation,
  usePostDatabaseReportAdminMutation,
  usePutDatabaseReportAdminMutation,
  usePostDocumentReportAdminMutation,
  usePutDocumentReportAdminMutation,
  useUploadDocumentReportAdminMutation,
  useGetReportsQuery,
  useGetReportQuery,
  useSendReportMutation,
} = reportsApi
